import { FunctionsHttpError } from "@supabase/supabase-js";
import { translateSupabaseError } from "@/utils/errorHandlerUtils";

/**
 * Extrae el mensaje REAL de un error de `supabase.functions.invoke`.
 *
 * Cuando una edge function responde con un status no-2xx, supabase-js entrega
 * un `FunctionsHttpError` cuyo `.message` es el genérico "Edge Function
 * returned a non-2xx status code" y esconde el cuerpo en `.context` (la
 * Response). Nuestras funciones (`grant-access`, `station-digest`, ...)
 * devuelven `{ error, detail }` con la causa concreta YA en español, así que
 * la leemos y hacemos passthrough. De paso, traducimos el rate-limit de envío
 * de correos de Supabase Auth (GoTrue) a un mensaje claro. Cualquier otro caso
 * (cuerpo ilegible, errores de red, etc.) pasa por la traducción central.
 *
 * @param error    El error devuelto por `supabase.functions.invoke`.
 * @param fallback Mensaje contextual en español si no hay causa utilizable.
 */
export async function extractEdgeFunctionError(
  error: unknown,
  fallback = "El servidor no pudo completar la operación. Intenta de nuevo."
): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const body = (await error.context.json()) as {
        error?: string;
        detail?: string;
      };
      const detail = body?.detail ?? "";
      // GoTrue limita los correos por dirección: "...you can only request this
      // after N seconds" / "email rate limit exceeded". Es lo que ocurre al
      // invitar/eliminar/re-invitar el mismo correo en pocos segundos.
      if (/rate limit|request this (?:after|once)/i.test(detail)) {
        return "Se alcanzó el límite de correos de Supabase Auth (por defecto 2/hora). Espera un poco antes de reintentar, o sube el límite habilitando SMTP propio (Resend) en el Dashboard.";
      }
      if (body?.error) {
        return detail ? `${body.error}: ${detail}` : body.error;
      }
    } catch {
      // Cuerpo no-JSON o ya consumido: caer a la traducción central.
    }
  }
  return translateSupabaseError(error, fallback).message;
}
